import { type ChangeEvent, type FC } from 'react';
import { format } from 'date-fns';

import { IconLoader2 } from '@tabler/icons-react';
import {
  ButtonField,
  ButtonGroup,
  ButtonGroupItem,
  InputField,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextareaField,
} from '@wk/components-react16';

import type { CheckpointSystemStatus } from '@/types';

import type {
  CheckpointDialogMode,
  CheckpointRow,
  MaintenanceCriticality,
} from './CheckpointSettingsPage.types';
import {
  getCurrentLocalDateTimeInput,
  MAINTENANCE_CRITICALITY_OPTIONS,
} from './CheckpointSettingsPage.utils';

interface CheckpointSettingsModalProps {
  isOpen: boolean;
  activeCheckpoint: CheckpointRow | null;
  targetStatus: CheckpointSystemStatus | null;
  dialogMode: CheckpointDialogMode;
  openedAtIso: string | null;
  comment: string;
  endDate: string;
  criticality: MaintenanceCriticality;
  endDateError?: string;
  isUpdatingCheckpoint: boolean;
  isSaveDisabled: boolean;
  onChange: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => void;
  onSubmit: () => void;
  onClose: () => void;
}

const CheckpointSettingsModal: FC<CheckpointSettingsModalProps> = ({
  isOpen,
  activeCheckpoint,
  targetStatus,
  dialogMode,
  openedAtIso,
  comment,
  endDate,
  criticality,
  endDateError,
  isUpdatingCheckpoint,
  isSaveDisabled,
  onChange,
  onSubmit,
  onClose,
}) => {
  const isEditDateDialog = dialogMode === 'EDIT_DATE';

  return (
    <Modal
      isOpen={isOpen}
      srLabelledBy="preview-modal-title"
      srDescribedBy="preview-modal-description"
      onUserRequest={onClose}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <ModalHeader
          titleId="preview-modal-title"
          isFullscreenButtonVisible
          onUserRequest={onClose}
        >
          {isEditDateDialog
            ? `Update date for ${activeCheckpoint?.displayName ?? ''}`
            : (
                <>
                  Move <span className="font-semibold">{activeCheckpoint?.displayName}</span>{' '}
                  to {targetStatus ?? 'LIVE'}
                </>
              )}
        </ModalHeader>
        <ModalBody descriptionId="preview-modal-description">
          {dialogMode === 'MOVE' ? (
            <>
              <TextareaField
                label={
                  targetStatus === 'MAINTENANCE'
                    ? 'Please provide a comment with the incident number and link to the bug'
                    : 'Please provide a reason for this change'
                }
                labelFor="preview-comment"
              >
                <textarea
                  value={comment}
                  placeholder="Reason"
                  id="preview-comment"
                  name="comment"
                  onChange={onChange}
                />
              </TextareaField>

              {targetStatus === 'MAINTENANCE' ? (
                <InputField label="Criticality">
                  <select
                    name="criticality"
                    value={criticality}
                    onChange={onChange}
                  >
                    {MAINTENANCE_CRITICALITY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </InputField>
              ) : null}

              {targetStatus === 'PREVIEW' ? (
                <div className="text-sm text-gray-600">
                  {(() => {
                    const baseIso = openedAtIso || new Date().toISOString();
                    const base = new Date(baseIso);
                    const endIso = new Date(
                      base.getTime() + 30 * 24 * 60 * 60 * 1000,
                    ).toISOString();
                    return (
                      <span>
                        This checkpoint will automatically be ACTIVE after 30 days on{' '}
                        {format(new Date(endIso), 'yyyy-MM-dd HH:mm')}.
                      </span>
                    );
                  })()}
                </div>
              ) : null}

              {targetStatus === 'MAINTENANCE' ? (
                <div className="text-sm text-gray-600">
                  {(() => {
                    const baseIso = openedAtIso || new Date().toISOString();
                    const base = new Date(baseIso);
                    const criticalityConfig = MAINTENANCE_CRITICALITY_OPTIONS.find(
                      (option) => option.value === criticality,
                    );
                    const days = criticalityConfig?.days ?? 7;
                    const endIso = new Date(
                      base.getTime() + days * 24 * 60 * 60 * 1000,
                    ).toISOString();
                    return (
                      <span>
                        This checkpoint will be automatically DISABLED if not taken out of Maintenance within {days}{' '}
                        day{days === 1 ? '' : 's'} (before {format(new Date(endIso), 'yyyy-MM-dd HH:mm')}).
                      </span>
                    );
                  })()}
                </div>
              ) : null}

              {targetStatus === 'DISABLED' ? (
                <div className="text-sm text-gray-600">
                  This checkpoint will be disabled immediately.
                </div>
              ) : null}
            </>
          ) : (
            <>
              <InputField label="Date (local time)">
                <input
                  type="datetime-local"
                  name="endDate"
                  value={endDate}
                  onChange={onChange}
                  min={getCurrentLocalDateTimeInput()}
                />
              </InputField>
              {endDateError ? (
                <p className="text-sm text-red-600">{endDateError}</p>
              ) : null}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup mode="static">
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField mode="text">
                <button
                  type="button"
                  id="preview-cancel"
                  onClick={onClose}
                >
                  Cancel
                </button>
              </ButtonField>
            </ButtonGroupItem>
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField>
                <button
                  disabled={isSaveDisabled}
                  id="preview-save"
                  type="submit"
                >
                  Save
                  {isUpdatingCheckpoint ? (
                    <IconLoader2 className="animate-spin" />
                  ) : null}
                </button>
              </ButtonField>
            </ButtonGroupItem>
          </ButtonGroup>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default CheckpointSettingsModal;
